import { memo, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  AlertTriangle, Check, Lock, LockOpen, Shapes, SkipForward, Square,
} from 'lucide-react'
import { useAlerts, useHasPermission, usePlayoutState } from '../../store/index.js'
import { usePlayout } from '../../hooks/usePlayout.js'
import { useOnAirState } from '../../hooks/useOnAirState.js'
import type { ScheduleItem } from '../../types/index.js'
import type { LiveFillEntry } from '../../lib/api.js'
import {
  formatDuration, formatDurationShort, formatTime, scheduleItemTitle, ITEM_TYPE_LABELS,
} from '../../lib/utils.js'
import { LiveSkipDialog } from './LiveSkipDialog.js'
import './OnAirBanner.css'

type BannerStyle = CSSProperties & { [key: `--${string}`]: string | number }

/** Compte à rebours mm:ss (ou h:mm:ss au-delà de l'heure). */
function formatCountdown(sec: number | null): string {
  if (sec == null) return '--:--'
  return formatDuration(sec)
}

/** Source du média joué : nom de fichier, source live ou clip d'interlude. */
function itemSourceLabel(item: ScheduleItem | null): string | null {
  if (!item) return null
  if (item.itemType === 'LIVE') {
    return item.liveSource?.sourceType === 'DECKLINK' ? 'Entrée Decklink' : 'Source Internet'
  }
  // Un item programme n'a pas toujours de mediaAsset direct : la lecture retombe
  // sur le premier asset non-proxy de la fiche (cf. itemPayloadPath côté engine),
  // on reproduit ce repli pour nommer le fichier plutôt qu'afficher « — ».
  return item.mediaAsset?.fileName
    ?? item.program?.mediaAssets?.[0]?.fileName
    ?? item.interludeClip?.name
    ?? null
}

/** Composition d'un bloc pub : nombre de spots, durée, annonceurs. */
function adBreakComposition(item: ScheduleItem): string | null {
  const placements = item.adPlacements ?? []
  if (placements.length === 0) return null
  const advertisers = [...new Set(
    placements
      .map(placement => placement.spot?.campaign?.advertiser?.name ?? placement.spot?.campaign?.name)
      .filter((name): name is string => Boolean(name)),
  )]
  const spotsLabel = `${placements.length} spot${placements.length > 1 ? 's' : ''}`
  return [spotsLabel, formatDurationShort(item.durationSec), advertisers.join(', ') || null]
    .filter(Boolean)
    .join(' · ')
}

function itemComposition(item: ScheduleItem | null): string | null {
  if (!item) return null
  if (item.itemType === 'AD_BREAK') return adBreakComposition(item)
  const source = itemSourceLabel(item)
  return [ITEM_TYPE_LABELS[item.itemType], formatDurationShort(item.durationSec), source]
    .filter(Boolean)
    .join(' · ')
}

// ─── Composant ────────────────────────────────────────────────────────────────

interface OnAirBannerProps {
  channelId: string | null
  /** `expanded` sur la Grille (112 px, trois panneaux), `compact` ailleurs (50 px). */
  variant:   'expanded' | 'compact'
}

export const OnAirBanner = memo(function OnAirBanner({ channelId, variant }: OnAirBannerProps) {
  const playout = usePlayoutState()
  const alerts  = useAlerts()
  const { onAirItem, nextItem, untilNext, nextLive, untilNextLive, progress, elapsed, remaining } = useOnAirState()

  const activeAlertCount = useMemo(() => alerts.filter(alert => alert.severity !== 'OK').length, [alerts])
  const title = scheduleItemTitle(onAirItem)
  const isLive = onAirItem?.itemType === 'LIVE'
  const bridgeConnected = playout?.bridgeConnected ?? false

  if (variant === 'compact') {
    return (
      <CompactBanner
        channelId={channelId}
        onAirItem={onAirItem}
        title={title}
        isLive={isLive}
        progress={progress}
        remaining={remaining}
        nextItem={nextItem}
        untilNext={untilNext}
        activeAlertCount={activeAlertCount}
        bridgeConnected={bridgeConnected}
      />
    )
  }

  return (
    <div className="tp-airbanner tp-airbanner--expanded">

      {/* ── En diffusion ──────────────────────────────────────────────────── */}
      <div className="tp-airbanner__current">
        <div className="tp-airbanner__eyebrow-row">
          <span className="tp-airbanner__onair">
            <span className="tp-airbanner__onair-dot" aria-hidden="true" />
            EN DIFFUSION
          </span>
          {onAirItem && (
            <>
              <span className="tp-airbanner__badge">{ITEM_TYPE_LABELS[onAirItem.itemType].toUpperCase()}</span>
              <span className="tp-airbanner__badge tp-airbanner__badge--outline">
                {isLive ? 'DIRECT' : 'FICHIER'}
              </span>
            </>
          )}
          <span className="tp-airbanner__source">{itemSourceLabel(onAirItem) ?? '—'}</span>
        </div>

        <div className="tp-airbanner__title">{title ?? 'Aucune diffusion en cours'}</div>

        <div className="tp-airbanner__progress-row">
          <div className="tp-airbanner__progress">
            <div className="tp-airbanner__progress-fill" style={{ '--tp-progress': `${progress}%` } as BannerStyle} />
          </div>
          <div className="tp-airbanner__times">
            <span>{formatDuration(elapsed)} écoulé</span>
            <span className="tp-airbanner__remaining">-{formatDuration(remaining)}</span>
            <span>{formatDuration(onAirItem?.durationSec ?? 0)} total</span>
          </div>
        </div>
      </div>

      {/* ── Suivant ───────────────────────────────────────────────────────── */}
      <div className="tp-airbanner__next">
        <div className="tp-airbanner__eyebrow">SUIVANT · BASCULE DANS</div>
        <div className="tp-airbanner__countdown-row">
          <span className="tp-airbanner__countdown">{formatCountdown(untilNext)}</span>
          {nextItem && (
            <span className={`tp-airbanner__preload${nextItem.status === 'LOADING' ? ' is-ready' : ''}`}>
              {nextItem.status === 'LOADING' ? <Check size={12} aria-hidden="true" /> : null}
              {nextItem.status === 'LOADING' ? 'PRÉCHARGÉ' : 'EN ATTENTE'}
            </span>
          )}
        </div>
        <div className="tp-airbanner__next-title">{scheduleItemTitle(nextItem) ?? 'Fin de grille'}</div>
        <div className="tp-airbanner__next-meta">{itemComposition(nextItem) ?? 'Aucun élément programmé après celui-ci'}</div>
      </div>

      {/* ── Prochain direct ───────────────────────────────────────────────── */}
      <div className="tp-airbanner__live">
        <div className="tp-airbanner__eyebrow">PROCHAIN DIRECT</div>
        {nextLive ? (
          <>
            <div className="tp-airbanner__countdown-row">
              <span className="tp-airbanner__countdown is-live">{formatCountdown(untilNextLive)}</span>
            </div>
            <div className="tp-airbanner__next-title">{scheduleItemTitle(nextLive)}</div>
            <div className="tp-airbanner__next-meta">
              {formatTime(nextLive.startTime).slice(0, 5)} · {itemSourceLabel(nextLive) ?? 'source non definie'}
            </div>
          </>
        ) : (
          <>
            <div className="tp-airbanner__countdown-row">
              <span className="tp-airbanner__countdown is-idle">--:--</span>
            </div>
            <div className="tp-airbanner__next-meta">Aucun direct programme sur la journee</div>
          </>
        )}
      </div>
    </div>
  )
})

// ─── Bandeau compact ──────────────────────────────────────────────────────────

const CompactBanner = memo(function CompactBanner({
  channelId, onAirItem, title, isLive, progress, remaining, nextItem, untilNext,
  activeAlertCount, bridgeConnected,
}: {
  channelId:        string | null
  onAirItem:        ScheduleItem | null
  title:            string | null
  isLive:           boolean
  progress:         number
  remaining:        number
  nextItem:         ScheduleItem | null
  untilNext:        number | null
  activeAlertCount: number
  bridgeConnected:  boolean
}) {
  const [locked, setLocked] = useState(true)
  const [liveSkipOpen, setLiveSkipOpen] = useState(false)
  // Contrôles antenne réservés à PLAYOUT_CONTROL ; masqués pour les rôles qui ne
  // peuvent pas les actionner (Journaliste) plutôt qu'affichés désactivés.
  const canControlPlayout = useHasPermission('PLAYOUT_CONTROL')
  const {
    endLiveAndFill, toggleInterlude, emergencyStop,
    skipPending, interludePending, emergencyPending, manualInterludeActive,
  } = usePlayout(channelId)

  const handleToggleLock = useCallback(() => setLocked(current => !current), [])

  // Le skip ouvre toujours l'aperçu du trou avant de modifier la grille.
  const handleSkip = useCallback(() => setLiveSkipOpen(true), [])

  const handleLiveSkipConfirm = useCallback(async (items: LiveFillEntry[], options: { compact: boolean }) => {
    if (!onAirItem) return
    await endLiveAndFill(onAirItem.id, items, options)
    setLiveSkipOpen(false)
  }, [onAirItem, endLiveAndFill])

  const handleLiveSkipCancel = useCallback(() => setLiveSkipOpen(false), [])
  const handleInterlude = useCallback(() => { void toggleInterlude() }, [toggleInterlude])
  const handleEmergency = useCallback(() => { void emergencyStop() }, [emergencyStop])

  const controlsDisabled = locked || !bridgeConnected

  return (
    <div className="tp-airbanner tp-airbanner--compact">
      <span className="tp-airbanner__onair">
        <span className="tp-airbanner__onair-dot" aria-hidden="true" />
        {isLive ? 'DIRECT' : 'EN DIFFUSION'}
      </span>

      <span className="tp-airbanner__compact-title">{title ?? 'Aucune diffusion en cours'}</span>

      {onAirItem && (
        <>
          <div className="tp-airbanner__progress tp-airbanner__progress--mini">
            <div className="tp-airbanner__progress-fill" style={{ '--tp-progress': `${progress}%` } as BannerStyle} />
          </div>
          <span className="tp-airbanner__remaining">-{formatDuration(remaining)}</span>
        </>
      )}

      <span className="tp-airbanner__compact-rule" aria-hidden="true" />

      <span className="tp-airbanner__compact-next">
        <span className="tp-airbanner__compact-label">SUIVANT</span>
        <span className="tp-airbanner__compact-next-title">{scheduleItemTitle(nextItem) ?? 'Fin de grille'}</span>
        <span className="tp-airbanner__compact-countdown">{formatCountdown(untilNext)}</span>
      </span>

      <span className={`tp-airbanner__alerts${activeAlertCount > 0 ? ' is-active' : ''}`}
        title={activeAlertCount > 0 ? `${activeAlertCount} alerte(s) antenne active(s)` : 'Aucune alerte active'}
      >
        <AlertTriangle size={13} aria-hidden="true" />
        {activeAlertCount}
      </span>

      {canControlPlayout && <span className="tp-airbanner__controls">
        <button
          type="button"
          className="tp-airbanner__control"
          onClick={handleSkip}
          disabled={locked || skipPending || !nextItem}
          title="Passer à l’élément suivant"
          aria-label="Passer à l’élément suivant"
        >
          <SkipForward size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`tp-airbanner__control${manualInterludeActive ? ' is-active' : ''}`}
          onClick={handleInterlude}
          disabled={controlsDisabled || interludePending}
          aria-pressed={manualInterludeActive}
          title="Basculer sur l’interlude"
          aria-label="Basculer sur l’interlude"
        >
          <Shapes size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="tp-airbanner__control tp-airbanner__control--danger"
          onClick={handleEmergency}
          disabled={controlsDisabled || emergencyPending}
          title="Arrêt d’urgence de l’antenne"
          aria-label="Arrêt d’urgence de l’antenne"
        >
          <Square size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`tp-airbanner__control${locked ? ' is-locked' : ''}`}
          onClick={handleToggleLock}
          aria-pressed={!locked}
          title={locked ? 'Déverrouiller les contrôles d’antenne' : 'Verrouiller les contrôles d’antenne'}
          aria-label={locked ? 'Déverrouiller les contrôles d’antenne' : 'Verrouiller les contrôles d’antenne'}
        >
          {locked ? <Lock size={14} aria-hidden="true" /> : <LockOpen size={14} aria-hidden="true" />}
        </button>
      </span>}

      {liveSkipOpen && channelId && onAirItem && (
        <LiveSkipDialog
          channelId={channelId}
          liveItem={onAirItem}
          nextItem={nextItem}
          onConfirm={handleLiveSkipConfirm}
          onCancel={handleLiveSkipCancel}
        />
      )}
    </div>
  )
})
